/** SouthIndianTradition — Hero. Temple gopuram with animated diyas. */

import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CalendarDays, MapPin, ChevronDown } from 'lucide-react';
import { SOUTH_INDIA_EASE } from '../theme';
import type { SouthIndianContent } from '../content';

// Gopuram-inspired decorative arch
function GopuramArch() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 flex items-start justify-center overflow-hidden">
      <svg
        viewBox="0 0 800 600"
        className="h-full w-full opacity-[0.07]"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Gopuram silhouette */}
        <path
          d="M200 600 L200 250 Q200 80 400 60 Q600 80 600 250 L600 600"
          fill="none"
          stroke="var(--si-mustard)"
          strokeWidth="6"
        />
        <path
          d="M160 600 L160 280 Q160 100 400 80 Q640 100 640 280 L640 600"
          fill="none"
          stroke="var(--si-mustard)"
          strokeWidth="3"
          opacity="0.5"
        />
        {/* Kalasham (top ornament) */}
        <circle cx="400" cy="50" r="18" fill="none" stroke="var(--si-mustard)" strokeWidth="3" />
        <circle cx="400" cy="50" r="8" fill="var(--si-mustard)" opacity="0.6" />
        {/* Tiered levels */}
        <line x1="300" y1="160" x2="500" y2="160" stroke="var(--si-mustard)" strokeWidth="2" opacity="0.4" />
        <line x1="260" y1="220" x2="540" y2="220" stroke="var(--si-mustard)" strokeWidth="2" opacity="0.4" />
        <line x1="230" y1="280" x2="570" y2="280" stroke="var(--si-mustard)" strokeWidth="2" opacity="0.3" />
      </svg>
    </div>
  );
}

// Animated diya (lamp) component
function Diya({ left, top, delay, size = 24 }: { left: string; top: string; delay: number; size?: number }) {
  return (
    <motion.div
      className="absolute"
      style={{ left, top, width: size, height: size }}
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
      transition={{ duration: 2.5, delay, repeat: Infinity, ease: 'easeInOut' }}
    >
      {/* Diya flame */}
      <svg viewBox="0 0 24 24" width={size} height={size}>
        <ellipse cx="12" cy="18" rx="6" ry="3" fill="var(--si-maroon)" opacity="0.6" />
        <path
          d="M12 2 C12 2 7 8 7 13 C7 17 9.5 19 12 19 C14.5 19 17 17 17 13 C17 8 12 2 12 2Z"
          fill="var(--si-mustard)"
          opacity="0.9"
        />
        <path
          d="M12 4 C12 4 9 9 9 13 C9 16 10.5 17.5 12 17.5 C13.5 17.5 15 16 15 13 C15 9 12 4 12 4Z"
          fill="#fff7e6"
          opacity="0.8"
        />
        <ellipse cx="12" cy="18" rx="4" ry="2" fill="var(--si-mustard-dark)" opacity="0.5" />
      </svg>
      {/* Glow */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(203,161,53,0.3) 0%, transparent 70%)',
          filter: 'blur(4px)',
        }}
      />
    </motion.div>
  );
}

// Decorative kolam / rangoli pattern
function KolamPattern() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 flex items-end justify-center overflow-hidden opacity-[0.04]">
      <svg viewBox="0 0 300 120" className="w-full max-w-4xl" preserveAspectRatio="xMidYMax meet">
        {Array.from({ length: 7 }, (_, i) => {
          const cx = 150 + (i - 3) * 40;
          return (
            <g key={i}>
              <circle cx={cx} cy={80} r={16 + i * 2} fill="none" stroke="var(--si-maroon)" strokeWidth="1" />
              <circle cx={cx - 8} cy={72} r={4} fill="var(--si-mustard)" />
              <circle cx={cx + 8} cy={72} r={4} fill="var(--si-maroon)" />
              <circle cx={cx} cy={88} r={4} fill="var(--si-green)" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function HeroSection({
  content,
  onRsvp,
}: {
  content: SouthIndianContent['hero'];
  onRsvp: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Diya placement — positioned along gopuram tiers
  const diyas = [
    { left: '12%', top: '18%', delay: 0 },
    { left: '85%', top: '18%', delay: 0.3 },
    { left: '18%', top: '32%', delay: 0.6 },
    { left: '78%', top: '32%', delay: 0.9 },
    { left: '22%', top: '46%', delay: 1.2 },
    { left: '72%', top: '46%', delay: 1.5 },
    { left: '10%', top: '60%', delay: 1.8 },
    { left: '86%', top: '60%', delay: 2.1 },
    { left: '46%', top: '12%', delay: 2.4 },
    { left: '50%', top: '52%', delay: 2.7 },
    { left: '30%', top: '70%', delay: 3.0 },
    { left: '66%', top: '70%', delay: 3.3 },
  ];

  return (
    <div ref={ref} className="relative">
      <header
        className="relative isolate flex min-h-[92svh] flex-col items-center justify-center overflow-hidden px-6 py-20 text-center sm:min-h-screen"
        style={{
          background: 'linear-gradient(170deg, var(--si-maroon-dark) 0%, var(--si-maroon) 40%, var(--si-green-dark) 100%)',
        }}
      >
        <GopuramArch />
        <KolamPattern />

        {/* Parallax background texture */}
        <motion.div
          className="absolute inset-0 -z-20 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url(${content.background})`,
            y: imageY,
          }}
        />

        {/* Animated diyas */}
        <div className="pointer-events-none absolute inset-0 z-0">
          {diyas.map((d, i) => (
            <Diya key={i} left={d.left} top={d.top} delay={d.delay} size={20 + (i % 3) * 6} />
          ))}
        </div>

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 mx-auto w-full max-w-5xl"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: SOUTH_INDIA_EASE }}
            className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.34em] text-[var(--si-mustard)]"
          >
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-px w-5 bg-[var(--si-mustard)]" />
              {content.kicker}
            </span>
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: SOUTH_INDIA_EASE }}
            className="font-serif text-[clamp(2.6rem,8vw,6.2rem)] font-semibold leading-[0.95] text-[var(--si-ivory)]"
          >
            <span className="bg-gradient-to-r from-[var(--si-mustard)] via-[var(--si-ivory)] to-[var(--si-mustard)] bg-clip-text text-transparent">
              {content.title}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: SOUTH_INDIA_EASE }}
            className="mt-3 font-serif text-lg italic text-[var(--si-ivory)]/80 sm:text-xl"
          >
            {content.subtitle}
          </motion.p>

          {/* Detail chips */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: SOUTH_INDIA_EASE }}
            className="mt-9 flex flex-wrap items-center justify-center gap-3"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--si-mustard)]/30 bg-[var(--si-maroon)]/40 px-4 py-2 text-sm text-[var(--si-ivory)] backdrop-blur-md">
              <CalendarDays size={16} className="text-[var(--si-mustard)]" />
              {content.dateLabel}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--si-mustard)]/30 bg-[var(--si-maroon)]/40 px-4 py-2 text-sm text-[var(--si-ivory)] backdrop-blur-md">
              <MapPin size={16} className="text-[var(--si-mustard)]" />
              {content.venueShort}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: SOUTH_INDIA_EASE }}
            className="mt-8 flex flex-wrap items-center justify-center gap-5"
          >
            <button
              type="button"
              onClick={onRsvp}
              className="rounded-full bg-[var(--si-mustard)] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--si-ink)] shadow-lg shadow-black/30 transition hover:-translate-y-0.5 hover:bg-[var(--si-mustard-dark)]"
            >
              RSVP Now
            </button>
            <span className="text-sm text-[var(--si-ivory)]/65">
              Hosted by {content.host}
            </span>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          aria-hidden
          animate={{ y: [0, 9, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1 text-[var(--si-mustard)]/80"
        >
          <span className="text-[0.6rem] uppercase tracking-[0.3em]">
            Scroll
          </span>
          <ChevronDown size={18} />
        </motion.div>
      </header>
    </div>
  );
}
